import { useCallback } from "react";
import { type EventData, STATUS } from "react-joyride";

import { handleTourScroll } from "./scrollTargetIntoView";

/**
 * TES-1659: Both DashboardTour and GuideTour need the SAME Joyride `onEvent`
 * handler — scroll the step target's real scroll container (`<main>`) into view
 * (joyride's window-only scroll engine misses it, so steps below the fold would
 * render off-screen), then run `completeTour()` when the tour finishes/skips.
 *
 * The two tours differ only in an optional finish side-effect (DashboardTour
 * resets `document.body.style.overflow`), so we share the handler here and take
 * that side-effect as an optional `onFinish` callback. Keeping it in one place
 * removes the previously-duplicated callback body from both components.
 */

/** Statuses at which a Joyride tour is considered finished. */
const FINISHED_STATUSES: readonly string[] = [STATUS.FINISHED, STATUS.SKIPPED];

export function useTourCallback(completeTour: () => void, onFinish?: () => void) {
  return useCallback(
    (data: EventData) => {
      handleTourScroll(data);

      if (FINISHED_STATUSES.includes(data.status)) {
        completeTour();
        onFinish?.();
      }
    },
    [completeTour, onFinish],
  );
}
